/**
 * File Storage Service
 *
 * Service layer for File Storage API v0.1 communication
 * Handles all HTTP requests related to file upload, download, and listing
 * Uses shared apiClient so auth (getAccessToken + 401 refresh) works in all environments.
 */

import {
  FileCategory,
  FileStatus,
  UploadResponse,
  FileListResponse,
  AutoUploadResponse,
  FolderListResponse,
} from '../../models/FileStorage';
import { API_ENDPOINTS, apiConfig } from '../../config/api';
import { apiClient } from '../api/apiClient';
import { autoUploadMap, uploadFiles } from './uploadMethods';
import { downloadFile, downloadLatest, downloadMultiple, triggerBrowserDownload } from './downloadMethods';
import { listFiles, listFolders, listAllFilesInFolder } from './listingMethods';
import { getAccessToken } from '../../utils/auth';

/**
 * File Storage Service Class
 *
 * Provides methods for:
 * - Auto-upload (map editor integration)
 * - File upload with progress tracking
 * - File listing with pagination
 * - File download (specific version and latest)
 */
export class FileStorageService {
  static autoUploadMap(
    projectId: string,
    file: File
  ): Promise<AutoUploadResponse> {
    return autoUploadMap(projectId, file);
  }

  static uploadFiles(
    projectId: string,
    category: FileCategory,
    files: File[],
    onProgress?: (progress: number) => void,
    mapId?: string
  ): Promise<UploadResponse> {
    return uploadFiles(projectId, category, files, onProgress, mapId);
  }

  static listFiles(
    projectId: string | number,
    category: FileCategory,
    page: number = 1,
    pageSize: number = 100,
    cctvId?: string,
    mapId?: string
  ): Promise<FileListResponse> {
    return listFiles(projectId, category, page, pageSize, cctvId, mapId);
  }

  static downloadFile(
    projectId: string,
    category: FileCategory,
    filename: string,
    mapId?: string
  ): Promise<Blob> {
    return downloadFile(projectId, category, filename, mapId);
  }

  static downloadLatest(
    projectId: string,
    category: FileCategory,
    originalName: string
  ): Promise<Blob> {
    return downloadLatest(projectId, category, originalName);
  }

  static downloadMultiple(
    projectId: string,
    category: FileCategory,
    filenames: string[]
  ): Promise<Blob> {
    return downloadMultiple(projectId, category, filenames);
  }

  /**
   * Delete file by filename
   */
  static async deleteFile(
    projectId: string,
    category: FileCategory,
    filename: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      const endpoint = API_ENDPOINTS.FILE_STORAGE.DELETE(projectId, category, filename);


      const response = await apiClient.delete(endpoint);

      return response.data;
    } catch (error) {
      console.error('[FileStorageService] Delete failed:', error);
      throw error;
    }
  }

  /**
   * Update file metadata (description and status)
   */
  static async updateFileMetadata(
    projectId: string,
    category: FileCategory,
    filename: string,
    data: {
      description?: string;
      status?: FileStatus;
    }
  ): Promise<{
    success: boolean;
    message: string;
    data: {
      filename: string;
      description?: string;
      status?: FileStatus;
    };
  }> {
    try {
      const endpoint = `/v0.1/filestorage/${projectId}/${category}/metadata/${encodeURIComponent(filename)}`;


      const response = await apiClient.patch(endpoint, data);

      return response.data;
    } catch (error) {
      console.error('[FileStorageService] Update metadata failed:', error);
      throw error;
    }
  }

  /**
   * Delete multiple files in batch
   */
  static async deleteFiles(
    projectId: string,
    category: FileCategory,
    filenames: string[]
  ): Promise<{ success: boolean; message: string }> {
    try {
      const endpoint = API_ENDPOINTS.FILE_STORAGE.BATCH_DELETE(projectId, category);


      const response = await apiClient.post(endpoint, { filenames });

      return response.data;
    } catch (error) {
      console.error('[FileStorageService] Batch delete failed:', error);
      throw error;
    }
  }

  /**
   * Delete folder and all its contents
   */
  static async deleteFolder(
    projectId: string,
    category: FileCategory,
    folderPath: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      const endpoint = API_ENDPOINTS.FILE_STORAGE.DELETE_FOLDER(projectId, category, folderPath);


      const response = await apiClient.delete(endpoint);

      return response.data;
    } catch (error) {
      console.error('[FileStorageService] Delete folder failed:', error);
      throw error;
    }
  }

  static triggerBrowserDownload(blob: Blob, filename: string): void {
    return triggerBrowserDownload(blob, filename);
  }

  /**
   * Format file size for display
   */
  static formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  }

  static listFolders(
    projectId: string,
    category: FileCategory,
    currentPath?: string
  ): Promise<FolderListResponse> {
    return listFolders(projectId, category, currentPath);
  }

  static listAllFilesInFolder(
    projectId: string,
    category: FileCategory,
    folderPath: string
  ): Promise<string[]> {
    return listAllFilesInFolder(projectId, category, folderPath);
  }

  /**
   * Run dedup filter on learning folder via SSE streaming.
   * Returns an abort function to cancel the operation.
   */
  static dedupFilter(
    projectId: string,
    folderPath: string,
    threshold: number,
    onProgress: (progress: { progress: number; currentFolder: string; processedFiles: number; totalFiles: number }) => void,
    onComplete: (result: { removedCount: number; savedBytes: number; processedFolders: number; totalFiles: number }) => void,
    onError: (message: string) => void,
  ): () => void {
    const controller = new AbortController();
    const endpoint = API_ENDPOINTS.FILE_STORAGE.DEDUP_FILTER(projectId);
    const url = `${apiConfig.BASE_URL}${endpoint}`;
    const token = getAccessToken();

    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ folderPath, threshold }),
      signal: controller.signal,
    }).then(async (response) => {
      if (!response.ok || !response.body) {
        onError(`서버 오류: ${response.status}`);
        return;
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let eventType = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            const data = line.slice(6).trim();
            try {
              const parsed = JSON.parse(data);
              if (eventType === 'progress') onProgress(parsed);
              else if (eventType === 'complete') onComplete(parsed);
              else if (eventType === 'error') onError(parsed.message || '알 수 없는 오류');
            } catch {
              // ignore parse errors
            }
            eventType = '';
          }
        }
      }
    }).catch((err) => {
      if (err.name !== 'AbortError') {
        onError(err.message || '네트워크 오류');
      }
    });

    return () => controller.abort();
  }

  /**
   * Format date for display (Korean format)
   */
  static formatDate(isoDate: string): string {
    const date = new Date(isoDate);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
}
